import { useMemo, useEffect, useRef } from 'react';

import { useFormContext } from 'react-hook-form';
import Asterisk from '@decisiv/iconix/lib/components/Asterisk';
import { Flex, Message, Loading } from '@decisiv/ui-components/lib/components';
import { TextField, Select } from 'components/Form';
import { DropdownListItem } from '@decisiv/ui-components';
import { P } from '@decisiv/ui-components/lib/components/Typography';

import { getCountries } from 'country-state-picker';
import { getStatesByCountry } from 'utils/countryRegions';

import { Trans, t } from '@lingui/macro';

import { FormContainer, InlineFields } from './styles';
import { useManagedPlatformAccountsQuery } from './hooks';

const CLEAR_STATE_SELECT = t`Clear State`;
const ALLOWED_COUNTRIES = ['US', 'CA', 'EC', 'MX', 'CL', 'JP'];
const COUNTRY_OPTIONS = getCountries()
  .filter(({ code }: { code: string }) =>
    ALLOWED_COUNTRIES.includes(code.toUpperCase()),
  )
  .map(({ code, name }: { code: string; name: string }) => ({
    id: code.toUpperCase(),
    label: name,
    value: code.toUpperCase(),
  }));

export default function DepotForm({ accountId }: { accountId?: string }) {
  const { watch, setValue } = useFormContext();
  const countryValue = watch('country');
  const previousCountryRef = useRef(countryValue);

  const {
    data: accounts,
    loading: loadingAccounts,
    loadingMore: loadingMoreAccounts,
    loadMore: loadMoreAccounts,
    hasNextPage: hasNextPageAccounts,
  } = useManagedPlatformAccountsQuery({
    variables: {
      page: 1,
    },
    skip: !!accountId,
  });

  const accountsOptions: DropdownListItem[] = useMemo(() => {
    if (!accounts?.managedPlatformAccounts?.collection) return [];

    const formattedData = accounts.managedPlatformAccounts.collection.map(
      (account) => ({
        id: account.id,
        label: account.name,
        value: account.id,
        sourceId: account.sourceId,
      }),
    );

    return formattedData;
  }, [accounts]);

  useEffect(() => {
    if (
      previousCountryRef.current &&
      previousCountryRef.current !== countryValue
    ) {
      setValue('state', '', { shouldValidate: true });
    }
    previousCountryRef.current = countryValue;
  }, [countryValue, setValue]);

  useEffect(() => {
    if (!hasNextPageAccounts || loadingAccounts || loadingMoreAccounts) return;
    loadMoreAccounts();
  }, [
    hasNextPageAccounts,
    loadingAccounts,
    loadingMoreAccounts,
    loadMoreAccounts,
  ]);

  const stateOptions = useMemo(
    () => (countryValue ? getStatesByCountry(countryValue) : []),
    [countryValue],
  );

  const LoadingOptions = () => (
    <P size="small" color="charcoal" paddingTop={1} paddingBottom={0.2}>
      <Loading size="small" title={t`Loading Accounts...`} />
    </P>
  );

  return (
    <FormContainer>
      <Flex justifyContent="flex-end">
        <Message color="information" icon={Asterisk}>
          <Trans>Required Fields</Trans>
        </Message>
      </Flex>
      {!accountId && (
        <Select
          label={t`Account`}
          name="accountId"
          options={accountsOptions}
          renderOptionLabel={loadingAccounts ? LoadingOptions : undefined}
          defaultValue=""
          required
        />
      )}
      <TextField
        label={t`ID`}
        name="id"
        helpMessage={t`Max 10 characters (alphanumeric only, no special characters)`}
        required
      />
      <TextField label={t`Name`} name="name" required />
      <TextField label={t`Email`} name="email" required />
      <TextField label={t`Address`} name="address" />
      <InlineFields>
        <TextField label={t`City`} name="city" required />
        <Select
          clearButtonLabel={CLEAR_STATE_SELECT}
          label={t`State`}
          name="state"
          options={stateOptions}
          defaultValue=""
          required
        />
      </InlineFields>
      <InlineFields>
        <TextField label={t`Postal Code`} name="postalCode" />
        <Select
          label={t`Country`}
          name="country"
          options={COUNTRY_OPTIONS}
          defaultValue=""
          required
        />
      </InlineFields>
    </FormContainer>
  );
}
